import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { store } from 'components/Store';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import useAddMetaTags from 'components/Dashboard/utilities/addMetaTags';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import {
  DateFilterButton,
  ChartViewOptions,
  TypeOfChartOptions,
} from 'components/Dashboard/Analytics/ui/AnalyticsFilters/FiltersBar';
import useEngagementStatsState from './useEngagementStatsState';
import useCallEngagementStats from './useCallEngagementStats';
import {
  COUNT_MODE_OPTIONS,
  BREAKDOWN_OPTIONS,
  resolveBreakdownLabel,
  sortBreakdownByQuantity,
} from './breakdownDimensions';
import seriesColours from './seriesColours';
import SingleSelectDropdown from './ui/SingleSelectDropdown';
import BreakdownLegend from './ui/BreakdownLegend';
import EngagementStatsChart from './ui/EngagementStatsChart';
import EngagementStatsDataTable from './ui/EngagementStatsDataTable';
import EngagementStatsRightPanel from './ui/EngagementStatsRightPanel';

const CrmEngagementStats = () => {
  const { addMetaTags } = useAddMetaTags();
  useEffect(() => {
    addMetaTags({ title: 'Engagement stats - People - PolicyMogul', hash: null });
  }, []);

  const globalState = useContext(store);
  const { state: globalStoreState, dispatch } = globalState;
  const teamMembers = globalStoreState?.team?.allMembers ?? [];
  const teamName = globalStoreState?.team?.teamName;
  const agencyName = globalStoreState?.activePlan?.organisationName ?? teamName;

  const { state, modifyState } = useEngagementStatsState();
  const { series, breakdown, isLoading } = useCallEngagementStats(state);
  const [activeCell, setActiveCell] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [hiddenBuckets, setHiddenBuckets] = useState([]);
  const [heightContainer, containerRef] = useHeightContainer();
  const chartRef = useRef(null);
  const chartSectionRef = useRef(null);
  const dataTableSectionRef = useRef(null);

  useEffect(() => {
    setHiddenBuckets([]);
  }, [state.breakdownBy]);

  const labelFor = useCallback(
    (bucket) => resolveBreakdownLabel({ breakdownBy: state.breakdownBy, bucket, teamMembers, teamName, agencyName }),
    [state.breakdownBy, teamMembers, teamName, agencyName]
  );

  const sortedBreakdown = useMemo(() => sortBreakdownByQuantity(breakdown), [breakdown]);

  const toggleBucket = (bucket, forceHidden) => {
    const isHidden = hiddenBuckets.includes(bucket);
    const nextHidden = forceHidden ?? !isHidden;
    if (nextHidden === isHidden) return;
    if (nextHidden && sortedBreakdown.length - hiddenBuckets.length <= 1) {
      createNotification('danger', 'You must have at least 1 resource selected');
      return;
    }
    setHiddenBuckets(nextHidden ? [...hiddenBuckets, bucket] : hiddenBuckets.filter((item) => item !== bucket));
  };

  const chartKeys = useMemo(
    () =>
      sortedBreakdown
        .map((item, index) => ({
          bucket: item.bucket,
          label: labelFor(item.bucket),
          colour: seriesColours[index % seriesColours.length],
        }))
        .filter((item) => !hiddenBuckets.includes(item.bucket)),
    [sortedBreakdown, hiddenBuckets, labelFor]
  );

  useEffect(() => {
    dispatch({
      type: 'MODIFY_SECTION',
      parameter: 'engagementStatsChartState',
      value: { chartSectionRef, localState: state, chartKeys },
    });
    return () => {
      dispatch({ type: 'MODIFY_SECTION', parameter: 'engagementStatsChartState', value: {} });
    };
  }, [state, chartKeys]);

  return (
    <div className='main-content pr-xl-5 px-md-4 px-3 mt-2'>
      <div ref={containerRef} />
      <div className='d-flex' style={{ height: `${heightContainer}px` }}>
        <div className='flex-grow-1 bg-main-white border rounded-top' style={{ minWidth: 0 }}>
          <CustomScrollbar className='simple-scrollbar smooth-scrolling' style={{ height: '100%' }}>
            <div className='px-lg-5 px-3'>
              <div className='mt-5 mb-4'>
                <CustomScrollbar className='chart-buttons-scrollbar'>
                  <div className='d-flex align-items-center'>
                    <SingleSelectDropdown
                      options={COUNT_MODE_OPTIONS}
                      selectedValue={state.countMode}
                      onSelect={(value) => modifyState({ property: 'countMode', newValue: value })}
                      primary
                    />
                    <div className='ml-3'>
                      <SingleSelectDropdown
                        options={BREAKDOWN_OPTIONS}
                        selectedValue={state.breakdownBy}
                        onSelect={(value) => modifyState({ property: 'breakdownBy', newValue: value })}
                      />
                    </div>
                    <div className='ml-3'>
                      <DateFilterButton analyticsState={state} modifyFiltersBar={modifyState} customLeftValue={0} />
                    </div>
                    <TypeOfChartOptions analyticsState={state} modifyFiltersBar={modifyState} />
                    <ChartViewOptions analyticsState={state} modifyFiltersBar={modifyState} />
                  </div>
                </CustomScrollbar>
              </div>
              <BreakdownLegend
                breakdown={sortedBreakdown}
                labelFor={labelFor}
                hiddenBuckets={hiddenBuckets}
                onToggle={toggleBucket}
              />
              <div className='mt-4' ref={chartSectionRef}>
                <EngagementStatsChart
                  ref={chartRef}
                  breakdown={sortedBreakdown}
                  hiddenBuckets={hiddenBuckets}
                  labelFor={labelFor}
                  typeOfChart={state.typeOfChart}
                  currentView={state.currentView}
                  countMode={state.countMode}
                  isLoading={isLoading}
                  activeSection={activeSection}
                  setActiveSection={setActiveSection}
                  onBarClick={() => dataTableSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                />
              </div>
              <div ref={dataTableSectionRef}>
                <EngagementStatsDataTable
                  totalSeries={series?.[0]}
                  breakdown={sortedBreakdown}
                  labelFor={labelFor}
                  currentView={state.currentView}
                  activeCell={activeCell}
                  onCellClick={(cell) => setActiveCell(cell)}
                  activeSection={activeSection}
                  setActiveSection={setActiveSection}
                />
              </div>
            </div>
          </CustomScrollbar>
        </div>
        {activeCell && (
          <EngagementStatsRightPanel
            cell={activeCell}
            breakdownBy={state.breakdownBy}
            currentView={state.currentView}
            onClose={() => setActiveCell(null)}
          />
        )}
      </div>
    </div>
  );
};

export default CrmEngagementStats;
